import { useCallback, useState } from "react";
import useDatepicker, { DatepickerHookProps } from "../useDatepicker";
import useInput, { InputHookProps } from "../useInput";
import useInputSelect, { InputSelectHookProps } from "../useInputSelect";
import useSelect, { SelectHookProps } from "../useSelect";
import useTimepicker, { TimepickerHookProps } from "../useTimepicker";

import { CreateOrderParam } from "@/api/order/interface";
import { padTo2Digits } from "@/helpers";
import { useApi } from "@/api";

export interface FieldsReturn {
  addressString: InputHookProps;
  flat: InputHookProps;
  doorNumber: InputHookProps;

  appointmentDate: DatepickerHookProps;
  appointmentTime: TimepickerHookProps;
  appointmentDuration: InputHookProps;
  contactVendorDirectly: InputSelectHookProps;
  notifyViaSMS: InputSelectHookProps;
  sendNotifications: InputSelectHookProps;

  poNumber: InputHookProps;
  bedrooms: InputHookProps;
  floorArea: InputHookProps;
  parking: InputHookProps;
  virtualTourPartial: InputSelectHookProps;

  keys: InputSelectHookProps;
  keysTime: TimepickerHookProps;
  keysTimeOption: SelectHookProps;
  keysWith: SelectHookProps;
  keysWithDetail: InputHookProps;
  keyNotes: InputHookProps;

  allowFreelance: InputSelectHookProps;
  orderOfferPrice: InputHookProps;
  preferredWorker: InputHookProps;

  orderExtraChargeDescription: InputHookProps;
  orderExtraChargePriceExVat: InputHookProps;

  orderReference: InputHookProps;

  orderState: SelectHookProps;

  photoCount: InputHookProps;

  daysToPayInvoices: InputHookProps;
  email: InputHookProps;
  firstName: InputHookProps;
  lastName: InputHookProps;
  login: InputHookProps;
  phoneNumber: InputHookProps;

  furnitureNames: InputHookProps;

  imageMotionRequested: InputSelectHookProps;
  videoMotionRequested: InputSelectHookProps;
  virtualStagingRequested: InputSelectHookProps;

  fieldsHaveChanges: (data: CreateOrderParam) => boolean;
  resetFields: () => void;
  storeFieldsData: (data: CreateOrderParam) => void;
  updateFields: () => void;
}

const useFields = (): FieldsReturn => {
  const { order } = useApi();
  const [fieldsData, setFieldsData] = useState('');
  
  const addressString = useInput(null, [(v) => [v.length > 0, 'Please choose property address']]);
  const flat = useInput();
  const doorNumber = useInput();

  const appointmentDate = useDatepicker();
  const appointmentTime = useTimepicker();
  const appointmentDuration = useInput();
  const contactVendorDirectly = useInputSelect();
  const notifyViaSMS = useInputSelect();

  const sendNotifications = useInputSelect("true", true);
 
  const poNumber = useInput();
  const bedrooms = useInput(null, [(v) => [Number(v) > 0, 'Please specify the number of bedrooms']]);
  const floorArea = useInput();
  const parking = useInput();
  const virtualTourPartial = useInputSelect();

  const keys = useInputSelect();
  const keysTime = useTimepicker();
  const keysTimeOption = useSelect('Any time');
  const keysWith = useSelect('Agent');
  const keysWithDetail = useInput();
  const keyNotes = useInput();

  const allowFreelance = useInputSelect();
  const orderOfferPrice = useInput();
  const preferredWorker = useInput();

  const orderExtraChargeDescription = useInput();
  const orderExtraChargePriceExVat = useInput();

  const orderReference = useInput();

  const orderState = useSelect('0');

  const photoCount = useInput('0');

  const daysToPayInvoices = useInput();
  const email = useInput();
  const firstName = useInput();
  const lastName = useInput();
  const login = useInput();
  const phoneNumber = useInput();

  const furnitureNames = useInput();

  const imageMotionRequested = useInputSelect();
  const videoMotionRequested = useInputSelect();
  const virtualStagingRequested = useInputSelect();

  const storeFieldsData = useCallback((data: CreateOrderParam) => {
    setFieldsData(JSON.stringify(data));
  }, []);

  const fieldsHaveChanges = useCallback((data: CreateOrderParam) => {
    const newData = JSON.stringify(data);

    return newData === fieldsData;
  }, [fieldsData]);
  
  const resetFields = useCallback(() => {
    addressString.setValue('');
    flat.setValue('');
    doorNumber.setValue('');

    appointmentDate.setValue(null);
    appointmentTime.setValue('');
    appointmentDuration.setValue('');
    contactVendorDirectly.setChecked(false);
    notifyViaSMS.setChecked(false);

    sendNotifications.setChecked(true);

    poNumber.setValue('');
    bedrooms.setValue('');
    floorArea.setValue('');
    parking.setValue('');
    virtualTourPartial.setChecked(false);

    keys.setChecked(false);
    keysTime.setValue('');
    keysTimeOption.setValue('Any time');
    keysWith.setValue('Agent');
    keysWithDetail.setValue('');
    keyNotes.setValue('');

    orderState.setValue('0');

    allowFreelance.setChecked(false);
    orderOfferPrice.setValue('');
    preferredWorker.setValue('');

    daysToPayInvoices.setValue('');
    email.setValue('');
    firstName.setValue('');
    lastName.setValue('');
    login.setValue('');
    phoneNumber.setValue('');

    furnitureNames.setValue('');

    imageMotionRequested.setChecked(false);
    videoMotionRequested.setChecked(false);
    virtualStagingRequested.setChecked(false);
  }, []);

  const updateFields = useCallback(() => {
    const { orderDetails } = order;

    if (orderDetails) {
      addressString.setValue(orderDetails.propertyAddress.addressString || '');
      flat.setValue(orderDetails.propertyAddress.flat || '');
      doorNumber.setValue(orderDetails.propertyAddress.doorNumber || '');
  
      const appointmentDateTimeValue = orderDetails.common.appointmentDateTime ?  new Date(orderDetails.common.appointmentDateTime.replace('Z', '')) : null;
      //appointmentDateTimeValue?.setMinutes(appointmentDateTimeValue.getMinutes() + appointmentDateTimeValue.getTimezoneOffset());
      appointmentDate.setValue(appointmentDateTimeValue || null);
      appointmentTime.setValue(appointmentDateTimeValue ? `${padTo2Digits(appointmentDateTimeValue.getHours())}:${padTo2Digits(appointmentDateTimeValue.getMinutes())}` : null);
      appointmentDuration.setValue(`${orderDetails.common.appointmentDuration}`);
      contactVendorDirectly.setChecked(orderDetails.common.contactVendorDirectly);
      notifyViaSMS.setChecked(orderDetails.common.notifyViaSMS);
      sendNotifications.setChecked(orderDetails.common.sendNotifications);
  
      poNumber.setValue(`${orderDetails.poNumber}`);
      bedrooms.setValue(`${orderDetails.bedrooms}`);
      floorArea.setValue(`${orderDetails.floorArea}`);
      parking.setValue(orderDetails.parking);
      virtualTourPartial.setChecked(orderDetails.virtualTourPartial);
  
      keys.setChecked(orderDetails.keys);
      keysTime.setValue(orderDetails.keysTime);
      keysTimeOption.setValue(orderDetails.keysTimeOption || 'Any time');
      keysWith.setValue(orderDetails.keysWith || 'Agent');
      keysWithDetail.setValue(orderDetails.keysWithDetail);
      keyNotes.setValue(orderDetails.notes);

      orderExtraChargeDescription.setValue(orderDetails.orderExtraCharge.orderExtraChargeDescription);
      orderExtraChargePriceExVat.setValue(`${orderDetails.orderExtraCharge.orderExtraChargePriceExVat}`);

      orderOfferPrice.setValue(`${orderDetails.orderOfferPrice}`);

      orderState.setValue(`${orderDetails.orderState}`);

      photoCount.setValue(`${orderDetails.photoCount}`)

      daysToPayInvoices.setValue(`${orderDetails.accountDetails.daysToPayInvoices}`);
      email.setValue(orderDetails.accountDetails.accountsEmailAddress);
      firstName.setValue(orderDetails.accountDetails.firstName);
      lastName.setValue(orderDetails.accountDetails.lastName);
      login.setValue(orderDetails.accountDetails.name);
      phoneNumber.setValue(orderDetails.accountDetails.accountPhoneNumber ?? '');

      furnitureNames.setValue('');

      imageMotionRequested.setChecked(orderDetails.imageMotionRequested);
      videoMotionRequested.setChecked(orderDetails.videoMotionRequested);
      virtualStagingRequested.setChecked(orderDetails.virtualStagingRequested);
    }
  }, [order.orderDetails]);

  return {
    addressString,
    allowFreelance,
    appointmentDate,
    appointmentDuration,
    appointmentTime,
    bedrooms,
    contactVendorDirectly,
    daysToPayInvoices,
    doorNumber,
    email,
    fieldsHaveChanges,
    firstName,
    flat,
    floorArea,
    furnitureNames,
    imageMotionRequested,
    keyNotes,
    keys,
    keysTime,
    keysTimeOption,
    keysWith,
    keysWithDetail,
    lastName,
    login,
    notifyViaSMS,
    orderExtraChargeDescription,
    orderExtraChargePriceExVat,
    orderOfferPrice,
    orderReference,
    orderState,
    parking,
    phoneNumber,
    photoCount,
    poNumber,
    preferredWorker,
    resetFields,
    sendNotifications,
    storeFieldsData,
    updateFields,
    videoMotionRequested,
    virtualStagingRequested,
    virtualTourPartial
  }
}

export default useFields;